// usage-tab.tsx — Usage tab dispatcher for the Operations service detail page.
// Dispatches to the correct analytics view by service category:
//   cdn / waap  → CdnUsageView (bandwidth via CDN analytics backbone)
//   media       → MediaUsageView (LMS + VMS transcode minutes per day)
//   storage     → EmptyState (storage category uses its own Storage tab)
//   compute / registrar / other → EmptyState (no usage backbone yet)
import { useState } from 'react';
import { BarChart2 } from 'lucide-react';
import { Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { EmptyState, TimeRangePicker } from '@vnetwork/ui/data';
import type { OpsCategory } from '@/api/operations';
import { CdnUsageView } from './usage-cdn-view';
import { MediaUsageView } from './usage-media-view';

/* ── Date helpers ─────────────────────────────────────────────────────────── */

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

/* ── Date range picker ────────────────────────────────────────────────────── */

interface DateRangePickerProps {
  fromDate: string;
  toDate: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onReset: () => void;
}

function DateRangePicker({ fromDate, toDate, onFromChange, onToChange, onReset }: DateRangePickerProps) {
  return (
    <Card>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
            Date range
          </span>
          <TimeRangePicker
            showLive={false}
            presets={[]}
            from={fromDate}
            to={toDate}
            triggerLabel={fromDate && toDate ? `${fromDate} → ${toDate}` : 'Select dates'}
            onApply={(v) => {
              if (v.from && v.to) {
                onFromChange(v.from.slice(0, 10));
                onToChange(v.to.slice(0, 10));
              }
            }}
          />
          <Button size="sm" variant="secondary" onClick={onReset}>Last 30 days</Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Props ────────────────────────────────────────────────────────────────── */

interface UsageTabProps {
  serviceId: string;
  category: OpsCategory | string;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * UsageTab owns the shared date range state and dispatches to the right view.
 * Storage has its own dedicated tab (StorageTab) so it returns null here —
 * the parent page routes storage to the Storage tab, not the Usage tab.
 */
export function UsageTab({ serviceId, category }: UsageTabProps) {
  const [fromDate, setFromDate] = useState(() => toLocalDateStr(daysAgo(30)));
  const [toDate, setToDate] = useState(() => toLocalDateStr(new Date()));

  const handleReset = () => {
    setFromDate(toLocalDateStr(daysAgo(30)));
    setToDate(toLocalDateStr(new Date()));
  };

  // Categories with no usage backbone — show a clear empty state.
  if (category === 'compute' || category === 'registrar' || category === 'other' || category === 'storage') {
    return (
      <Card>
        <CardBody>
          <EmptyState
            icon={<BarChart2 size={28} />}
            title="Usage not available for this category"
            description={
              category === 'storage'
                ? 'Storage usage is available on the Storage tab.'
                : `Analytics are available for CDN, WAAP, and Media services. This service is categorised as "${category}".`
            }
          />
        </CardBody>
      </Card>
    );
  }

  const picker = (
    <DateRangePicker
      fromDate={fromDate}
      toDate={toDate}
      onFromChange={setFromDate}
      onToChange={setToDate}
      onReset={handleReset}
    />
  );

  if (category === 'cdn' || category === 'waap') {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {picker}
        <CdnUsageView serviceId={serviceId} fromDate={fromDate} toDate={toDate} />
      </div>
    );
  }

  if (category === 'media') {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {picker}
        <MediaUsageView serviceId={serviceId} fromDate={fromDate} toDate={toDate} />
      </div>
    );
  }

  // Fallback for any future unknown category.
  return (
    <Card>
      <CardBody>
        <EmptyState
          icon={<BarChart2 size={28} />}
          title="Usage not supported"
          description={`No analytics available for category "${category}".`}
        />
      </CardBody>
    </Card>
  );
}
